import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import type { RootState } from "@/store";
import { grab_base_url } from "@/utils/api_methods";
import { clearAuth, setAuthError } from "@/features/auth/authSlice";

/* 
TODO: Add a proxy to fetch at '/api' base_url based on environment
- Removes full api-gateway url
- Adds proxy to fetch at '/dev-api'
- Adds proxy to fetch at '/staging-api'
- Adds proxy to fetch at '/admin-dev-api'
- Adds proxy to fetch at '/admin-staging-api'
- Should be done through vite.config.ts in combination with .env.{environment} files
- Switching between environments could be done through package.json scripts setting env variables (for development)
*/
const baseQuery = fetchBaseQuery({
  baseUrl: grab_base_url(window.location.origin, "https"),
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.auth.token;

    if (token) {
      try {
        const decoded = jwtDecode<JwtPayload>(token);
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          // Token expired - will be handled by error middleware
          return headers;
        }
      } catch {
        // Invalid token - will be handled by error middleware
        return headers;
      }

      headers.set("x-pulse-jwt", token);
    }
    return headers;
  },
});

const baseQueryWithErrorHandling = async (
  args: any,
  api: any,
  extraOptions: any,
) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    const error = result.error as { status: string; data: { message: string } };
    if (error.status === "401" || error.status === "410") {
      api.dispatch(setAuthError("Session expired. Please log in again."));
      api.dispatch(clearAuth());
      window.location.href = "/";
      return result;
    }
  }
  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    "User",
    "Org",
    "OrgMetadata",
    "OrgUsers",
    "Preferences",
    "AlertChecks",
    "ApiKeys",
    "UserPortfolios",
    "OrgPortfolios",
    "ManualAdjustments",
    "StaticPriceOverrides",
    "BlotterDropcopy",
    "Strategies",
    "AccountStrategy",
    "Account",
    "Portfolio",
    "ApiKey",
    "VenueCodes",
    "DropcopyIps",
    "UserRoles",
  ],
  endpoints: () => ({}),
});
